import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import {
  ID_CHECK,
  ID_FORMAT,
  NAME_CHECK,
  checkInputErrorOnlyNumbers,
  checkInputErrorRegEx,
  checkInputErrorRequired,
  formatRegEx,
  formatOnlyNumbers,
  useAnalyticsEvent,
  useAnalyticsPageView,
  useEffectOnce,
  useLanguage,
  useLanguageFlags
} from '@chilecompra/react-kit'
import {
  Button,
  Input,
  Select,
  Toast,
  useCheckBox,
  useInput,
  useSelect,
  useViewport
} from '@chilecompra/react-kit/components'
import { FOREIGN_GENDER_OPTIONS } from '../../config/settings/constants'
import { isLocalEnv, REACT_APP_HELP_URL } from '../../config/settings/environment'
import routes from '../../config/settings/routes'
import {
  checkInputNationalDniError,
  checkInputErrorMinLength,
  checkInputErrorMaxLength
} from '../../modules/utils/checkers'
import {
  onGetCountriesThunk,
  onGetSellerBlockedInfoThunk,
  onResetRegisterWizardState,
  onSaveTemporaryPersonalInformation
} from './RegisterWizard.actions'
import {
  CheckBox,
  Conditions,
  DesktopNavigation,
  Hidden,
  Image,
  Instructions,
  Label,
  Link,
  LinkHelp,
  MobileNavigation,
  PhoneSection,
  PhoneContent,
  Required,
  Strong,
  Root,
  Section,
  Title
} from './RegisterWizardForeignPersonalInformation.styles'

/**
 * The RegisterWizardForeignPersonalInformation's container.
 */
const RegisterWizardForeignPersonalInformation = props => {
  const { onChangeStep } = props
  const { blockedInfo, countries, error, loading } = useSelector(state => state.registerWizard)
  const { size } = useViewport()
  const dispatch = useDispatch()
  const { language } = useLanguage()
  const { hasLanguage, isLanguageES } = useLanguageFlags()
  const navigate = useNavigate()
  const { value: checked, onChange: handleChange } = useCheckBox()
  const {
    error: identificationNumberError,
    value: identificationNumberValue,
    onChange: handleIdentificationNumberChange
  } = useInput({
    errorCallbacks: [
      checkInputErrorRequired(language('RegisterWizardForeignPersonalInformation__fieldRequiredError')),
      checkInputErrorRegEx(ID_CHECK, language('RegisterWizardForeignPersonalInformation__fieldInvalidError'))
    ],
    formatCallbacks: [value => formatRegEx(value, ID_FORMAT)]
  })
  const {
    error: nameError,
    value: nameValue,
    onChange: handleNameChange
  } = useInput({
    errorCallbacks: [
      checkInputErrorRequired(language('RegisterWizardForeignPersonalInformation__fieldRequiredError')),
      checkInputErrorRegEx(NAME_CHECK, language('RegisterWizardForeignPersonalInformation__fieldInvalidError'))
    ]
  })
  const {
    error: lastNameError,
    value: lastNameValue,
    onChange: handleLastNameChange
  } = useInput({
    errorCallbacks: [
      checkInputErrorRequired(language('RegisterWizardForeignPersonalInformation__fieldRequiredError')),
      checkInputErrorRegEx(NAME_CHECK, language('RegisterWizardForeignPersonalInformation__fieldInvalidError'))
    ]
  })
  const {
    error: phoneError,
    value: phoneValue,
    onChange: handlePhoneChange
  } = useInput({
    errorCallbacks: [
      checkInputErrorRequired(language('RegisterWizardForeignPersonalInformation__fieldRequiredError')),
      checkInputErrorOnlyNumbers(language('RegisterWizardForeignPersonalInformation__fieldOnlyNumbersError')),
      checkInputErrorMaxLength(15, language('RegisterWizardForeignPersonalInformation__fieldMaxLengthError')),
      checkInputErrorMinLength(8, language('RegisterWizardForeignPersonalInformation__fieldMinLengthError'))
    ],
    formatCallbacks: [formatOnlyNumbers]
  })
  const {
    error: phoneCodeError,
    value: phoneCodeValue,
    onChange: onPhoneCodeChange
  } = useSelect({
    errorCallbacks: [checkInputErrorRequired(language('RegisterWizardForeignPersonalInformation__fieldRequiredError'))]
  })
  const {
    error: genderError,
    value: genderValue,
    onChange: onGenderChange
  } = useSelect({
    initialValue: 1,
    errorCallbacks: [checkInputErrorRequired(language('RegisterWizardForeignPersonalInformation__fieldRequiredError'))]
  })
  const {
    error: countryError,
    value: countryValue,
    onChange: onCountryChange
  } = useSelect({
    errorCallbacks: [checkInputErrorRequired(language('RegisterWizardForeignPersonalInformation__fieldRequiredError'))]
  })
  const handleBack = () => {
    dispatch(onResetRegisterWizardState())
    navigate(routes.registerWizard, { replace: true })
  }
  const [isDniChilean, setIsDniChilean] = useState(false)
  const handleCheck = checkInputNationalDniError()(identificationNumberValue)

  const handleNext = useCallback(
    event => {
      event.preventDefault()

      if (handleCheck) {
        setIsDniChilean(true)
      } else {
        dispatch(
          onGetSellerBlockedInfoThunk({
            dni: identificationNumberValue,
            checkPersonInformationCallback: () => {
              dispatch(
                onSaveTemporaryPersonalInformation({
                  identificationNumber: identificationNumberValue,
                  isForeign: hasLanguage,
                  country: countryValue,
                  gender: genderValue,
                  name: nameValue,
                  firstLastName: lastNameValue,
                  phoneCode: phoneCodeValue,
                  phone: phoneValue
                })
              )

              onChangeStep('email-verify')
            }
          })
        )
      }
    },
    [
      dispatch,
      onGetSellerBlockedInfoThunk,
      onSaveTemporaryPersonalInformation,
      onChangeStep,
      identificationNumberValue,
      hasLanguage,
      countryValue,
      genderValue,
      nameValue,
      lastNameValue,
      phoneCodeValue,
      phoneValue
    ]
  )

  const countryPhoneCodeFormat = useCallback(
    country => {
      if (isLanguageES) {
        if (country.spanishName === null || country.phoneAreaCode === null) {
          return
        }
        return `${country.spanishName} (+${country.phoneAreaCode})`
      }

      return `${country.englishName} (+${country.phoneAreaCode})`
    },
    [isLanguageES]
  )
  const countryPhoneCodeOptions = useMemo(
    () =>
      countries
        ?.filter(option => option.code !== 'CL')
        .map(
          option =>
            ({
              name: countryPhoneCodeFormat(option),
              value: option.id
            } || [])
        ),
    [countries, isLanguageES]
  )
  const genderOptions = useMemo(
    () => FOREIGN_GENDER_OPTIONS.map(option => ({ ...option, name: language(option.name) || option.name })),
    [isLanguageES]
  )
  const countriesOptions = useMemo(
    () =>
      countries
        ?.filter(option => option.code !== 'CL')
        .map(option => ({
          name: isLanguageES || !option.englishName ? option.spanishName : option.englishName,
          value: option.id
        })),
    [countries, isLanguageES]
  )
  const canNotSubmit = useCallback(() => {
    const hadErrors =
      identificationNumberError !== '' ||
      nameError !== '' ||
      lastNameError !== '' ||
      phoneError !== '' ||
      phoneCodeError !== '' ||
      genderError !== '' ||
      countryError !== ''
    const hadInvalidValues =
      identificationNumberValue === '' ||
      nameValue === '' ||
      lastNameValue === '' ||
      phoneValue === '' ||
      countryValue === ''

    return hadErrors || hadInvalidValues || error || !checked
  }, [checked, countryValue, error, identificationNumberValue, lastNameValue, nameValue, phoneValue])
  const disabledSubmit = !isLocalEnv && canNotSubmit()

  useEffectOnce(() => {
    dispatch(onGetCountriesThunk({ order: isLanguageES ? 'name' : 'englishName' }))
  })

  useAnalyticsEvent({ action: 'Inicia registro', category: 'registrar-usuario' })
  useAnalyticsPageView('/paso/1/informacion_personal')

  return (
    <Root>
      <Section container>
        <Section item xs={12} md={6}>
          <Title>{language('RegisterWizardForeignPersonalInformation__title')}</Title>
          <Instructions>{language('RegisterWizardForeignPersonalInformation__description')}</Instructions>
          <Section
            container
            direction="column"
            maxWidth={size.isDownToMedium && size.isUpToSmall ? '384px' : '100%'}
            margin="15px 0"
          >
            <Section item>
              <Input
                autoFocus
                required
                error={identificationNumberError}
                info={language('RegisterWizardForeignPersonalInformation__identificationNumberInputInfo')}
                label={language('RegisterWizardForeignPersonalInformation__identificationNumberInputLabel')}
                value={identificationNumberValue}
                onChange={handleIdentificationNumberChange}
              />
              {isDniChilean && (
                <Toast margin="20px 0" severity="warning" width="100%">
                  <br />
                  {language('RegisterWizardForeignPersonalInformation__identificationNumberAlert__1')}{' '}
                  <Link href="/registrarme">
                    {language('RegisterWizardForeignPersonalInformation__identificationNumberAlert__2')}
                  </Link>
                </Toast>
              )}
            </Section>
            <Section item>
              <Input
                required
                error={nameError}
                label={language('RegisterWizardForeignPersonalInformation__nameInputLabel')}
                value={nameValue}
                onChange={handleNameChange}
              />
            </Section>
            <Section item>
              <Input
                required
                error={lastNameError}
                label={language('RegisterWizardForeignPersonalInformation__lastNameInputLabel')}
                value={lastNameValue}
                onChange={handleLastNameChange}
              />
            </Section>
            <Section item>
              <Label>
                {language('RegisterWizardForeignPersonalInformation__phoneInputLabel')} <Required />
              </Label>
              <PhoneSection>
                <PhoneContent>
                  <Select
                    loading={loading}
                    onChange={onPhoneCodeChange}
                    options={countryPhoneCodeOptions}
                    placeholder={language('RegisterWizardForeignPersonalInformation__phoneInputPlaceholder')}
                    value={phoneCodeValue}
                  />
                </PhoneContent>
                <PhoneContent>
                  <Input
                    required
                    error={phoneError}
                    placeholder="81234567"
                    value={phoneValue}
                    onChange={handlePhoneChange}
                  />
                </PhoneContent>
              </PhoneSection>
            </Section>
            <Section item>
              <Select
                required
                error={genderError}
                label={language('RegisterWizardForeignPersonalInformation__genderSelectLabel')}
                loading={loading}
                onChange={onGenderChange}
                options={genderOptions}
                placeholder={language('RegisterWizardForeignPersonalInformation__countrySelectPlaceholder')}
                value={genderValue}
              />
            </Section>
            <Section item>
              <Select
                required
                error={countryError}
                label={language('RegisterWizardForeignPersonalInformation__countrySelectLabel')}
                loading={loading}
                onChange={onCountryChange}
                options={countriesOptions}
                placeholder={language('RegisterWizardForeignPersonalInformation__countrySelectPlaceholder')}
                value={countryValue}
              />
            </Section>
            <Section item>
              <Conditions>
                <CheckBox checked={checked} onChange={handleChange} />
                {language('RegisterWizardForeignPersonalInformation__terms__1')}{' '}
                <Link
                  href="https://www.chilecompra.cl/terminos-y-condiciones-de-uso/"
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  {language('RegisterWizardForeignPersonalInformation__terms__2')}
                </Link>
              </Conditions>
            </Section>
          </Section>
        </Section>
        <Hidden smDown>
          <Section item xs={12} md={6} margin="-35px 0 0" padding="0 0 0 51px">
            <Image src="/assets/img/steps/paso2.svg?h=e57104cc1427561c7d89c6004abefe05" alt="Registro vía ClaveÚnica" />
          </Section>
        </Hidden>
      </Section>

      {!blockedInfo.loading && (
        <>
          {blockedInfo.error && (
            <Toast
              margin="16px 0"
              width="100%"
              severity="warning"
              textDirection="vertical"
              title="Información temporalmente no disponible"
            >
              <br />
              En estos momentos no podemos mostrar esta información. Podría tratarse de una intermitencia en los
              servicios del <Strong>Servicio de Impuestos Internos</Strong>. Por favor, vuelve a intentarlo en unos
              minutos.
            </Toast>
          )}

          {!blockedInfo.error && blockedInfo.isBlocked && (
            <Toast margin="16px 0" width="100%" severity="warning">
              <br />
              Nuestros registros indican que la empresa ya se encuentra registrada y está bloqueada en la plataforma de
              Mercado Público debido a: <Strong>{blockedInfo.reason}</Strong> Si necesitas más información, solicítala a
              través de nuestro{' '}
              <LinkHelp href={REACT_APP_HELP_URL} rel="noreferrer" target="_blank">
                Centro de Ayuda.
              </LinkHelp>
            </Toast>
          )}
        </>
      )}

      {!size.isTiny && (
        <Section container margin="48px 0 0" maxWidth="568px">
          <DesktopNavigation>
            <Button onClick={handleBack} type="button" disabled={blockedInfo.loading} variant="text" width="265px">
              {language('RegisterWizardForeignPersonalInformation__back')}
            </Button>
            <Button
              color="primary"
              disabled={disabledSubmit || blockedInfo.loading}
              margin="0 0 0 30px"
              type="button"
              width="265px"
              onClick={handleNext}
            >
              {language('RegisterWizardForeignPersonalInformation__next')}
            </Button>
          </DesktopNavigation>
        </Section>
      )}
      {size.isTiny && (
        <MobileNavigation>
          <Button onClick={handleBack} type="button" disabled={blockedInfo.loading} variant="text" width="168px">
            {language('RegisterWizardForeignPersonalInformation__back')}
          </Button>
          <Button
            color="primary"
            disabled={disabledSubmit || blockedInfo.loading}
            type="button"
            width="168px"
            onClick={handleNext}
          >
            {language('RegisterWizardForeignPersonalInformation__next')}
          </Button>
        </MobileNavigation>
      )}
    </Root>
  )
}

RegisterWizardForeignPersonalInformation.defaultProps = {
  onChangeStep: () => undefined
}

RegisterWizardForeignPersonalInformation.propTypes = {
  onChangeStep: PropTypes.func
}

export default RegisterWizardForeignPersonalInformation
